import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { FormattedMessage, type MessageDescriptor } from "react-intl";

import { featureFlags } from "~/common/feature-flags";
import { PatientFormNotice } from "~/components/redesign-v1/patient-form/patient-form-notice";
import type { TPatient } from "~/types/patient";

import { describeCapability, detectCapability } from "./capability";
import { logClassifierUnavailable } from "./diagnostics";
import { classifierPreflight, type UnavailableReason } from "./preflight";

// Lazy chunk: the step + onnxruntime-web load only on a supported browser.
const LazyStep = lazy(() => import("./photo-classifier-step").then((m) => ({ default: m.PhotoClassifierStep })));

/** QA/Playwright override: `?pcForce=fallback` forces the legacy UI. */
function forcedFallback(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("pcForce") === "fallback";
}

/** What each reason tells the doctor. Support is named only where support is actually the next step. */
const REASON_MESSAGE: Record<UnavailableReason, MessageDescriptor["id"]> = {
  browser: "photo_classifier.unavailable_browser",
  config: "photo_classifier.unavailable_config",
  slow: "photo_classifier.unavailable_slow",
  error: "photo_classifier.unavailable_error",
};

interface PhotoClassifierEntryProps {
  patientId: TPatient["patient_id"];
  correctionNumber?: number;
  required?: boolean;
  /** The legacy 8-slot UI, rendered when the classifier is unsupported or degrades. */
  fallback: React.ReactNode;
  heading?: React.ReactNode;
}

/**
 * Chooses the photo UI: on a capable browser it shows ONLY the classifier's protocol grid;
 * otherwise — or if the model fails to load / projects too slow at classify time (`onDegrade`) — it
 * shows ONLY the legacy 8-slot `fallback`. Never both
 * (docs/features/photo-classifier.md, ADR-0001, improvements OQ-4).
 *
 * THE ANSWER IS SETTLED BEFORE THE DOCTOR UPLOADS ANYTHING (owner 2026-08-13). It used to be settled
 * on the Classify click: stage accepted a whole protocol, then failed to load the runtime and swapped
 * the UI out underneath the doctor with nothing said. Now `classifierPreflight()` runs on mount — it
 * compiles a WASM module (which is what consults the CSP) and HEADs the onnxruntime loader (which is
 * what catches a wrong MIME type) — so an environment that cannot run the classifier renders the
 * manual grid from the first paint, with a notice saying which kind of problem it is.
 *
 * EVERY FALLBACK IS EXPLAINED. Silently degrading is what made the stage failure hard to read: the
 * doctor saw a different UI and no reason for it. `browser` asks them to update, `config` and `error`
 * point at support, `slow` says it is this device.
 *
 * There is no manual "switch to previous version" any more (owner 2026-09-23): the step's own cards
 * each take an upload, so it IS the manual grid, and a second one to switch to would only split the
 * photos across two surfaces.
 *
 * REDESIGN-ONLY. The step is built from the redesign-v1 design system, whose colour tokens are scoped
 * to `[data-redesign-v1]` and have no legacy fallback — outside a redesign root it would render
 * unstyled. `FEATURE_REDESIGN_V1` is on in every deployed environment, so this costs nothing today;
 * it is a guard against the classifier being switched on for the legacy form, where it would look
 * broken. Flag off ⇒ the manual grid, exactly as before — and with no notice, because nothing is
 * wrong: that build simply has no classifier.
 */
export function PhotoClassifierEntry({ patientId, correctionNumber, required = true, fallback, heading }: PhotoClassifierEntryProps) {
  const offered = useMemo(() => featureFlags.redesignV1 && !forcedFallback(), []);
  const capability = useMemo(() => (offered ? detectCapability() : null), [offered]);
  const capable = capability?.supported === true;
  const [blocked, setBlocked] = useState<UnavailableReason | null>(() => (offered && !capable ? "browser" : null));
  const [checking, setChecking] = useState(capable);

  useEffect(() => {
    if (!capability) return undefined;
    if (!capability.supported) {
      // Answered here with no probe, so the preflight never sees this verdict — or logs it.
      logClassifierUnavailable("browser", describeCapability(capability));
      return undefined;
    }
    let live = true;
    void classifierPreflight().then((result) => {
      if (!live) return;
      if (!result.ok) setBlocked(result.reason ?? "config");
      setChecking(false);
    });
    return () => {
      live = false;
    };
  }, [capability]);

  if (!offered) {
    return (
      <>
        {heading}
        {fallback}
      </>
    );
  }

  if (blocked) {
    return (
      <>
        {heading}
        <div className="tw-flex tw-flex-col tw-gap-4">
          <PatientFormNotice testId="pc-unavailable-notice">
            <FormattedMessage id={REASON_MESSAGE[blocked]} />
          </PatientFormNotice>
          {fallback}
        </div>
      </>
    );
  }

  const loading = (
    <>
      {heading}
      <PhotoClassifierLoading />
    </>
  );

  if (checking) return loading;

  return (
    <Suspense fallback={loading}>
      <LazyStep patientId={patientId} correctionNumber={correctionNumber} required={required} onDegrade={setBlocked} heading={heading} />
    </Suspense>
  );
}

function PhotoClassifierLoading() {
  return (
    <div data-testid="pc-loading" className="tw-rounded-rd tw-border tw-border-solid tw-border-border tw-p-4 tw-text-sm tw-text-muted-foreground">
      <FormattedMessage id="photo_classifier.loading" />
    </div>
  );
}
